import React from 'react';
import { Helmet } from 'react-helmet';
import { redirect } from 'react-router';

import { fetchBillingAccount, fetchPrices } from '@/actions/billing';
import { fetchCountries } from '@/actions/country';
import { fetchGroup } from '@/actions/group';
import { notify } from '@/actions/ui';
import BillingSettings from '@/components/BillingSettings';
import ConsultationsStarting from '@/components/ConsultationsStarting';
import CreditPurchaseForm from '@/components/CreditPurchaseForm';
import CreditSettings from '@/components/CreditSettings';
import LayoutPage from '@/components/Layout/LayoutPage';
import Settings from '@/components/Settings';
import SubscriptionSettings from '@/components/SubscriptionSettings';
import config from '@/config';
import { ERROR_CODES } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';
import { formatAccountType } from '@/core/billing';
import { PermissionService } from '@/core/permissions';
import { UserContext, isGroupContext } from '@/core/user';
import { Viewer } from '@/core/viewer';
import NotFoundPage from '@/pages/NotFoundPage';
import { fetchProfile } from '@/profile/store';
import Branding from '@/routes/teamSettings/Branding';
import Preferences from '@/routes/teamSettings/Preferences';
import SavedSearches from '@/routes/teamSettings/SavedSearches';
import Subdomain from '@/routes/teamSettings/Subdomain';
import { AppStore } from '@/store';
import { fetchUser } from '@/store/user';

import { LegacyRoute } from '../routesMiddleware';
import Communication from './Communication';
import Emails from './Emails';
import Location from './Location';
import Privacy from './Privacy';
import Rate from './Rate';
import SignInSecurity from './SignInSecurity';
import TeamMembers from './TeamMembers';

interface NavItem {
  title: string;
  path: string;
  component: React.ComponentType<any>;
  componentProps?: Record<string, unknown>;
  id?: string;
  url?: boolean;
  hidden?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

/**
 * Parse the splat path to determine personal vs team settings context.
 *
 * Personal: /settings/emails, /settings/billing/purchase
 * Team:     /settings/team/qa-smb-billing/subscription
 */
export function parsePath(splat: string | undefined): {
  isTeam: boolean;
  teamSlug: string | null;
  path: string | null;
} {
  if (!splat) return { isTeam: false, teamSlug: null, path: null };

  const parts = splat.split('/').filter(Boolean);
  if (parts[0] === 'team' && parts.length >= 2 && parts[1]) {
    const teamSlug = parts[1];
    const rest = parts.slice(2).join('/') || null;
    return { isTeam: true, teamSlug, path: rest };
  }

  return { isTeam: false, teamSlug: null, path: splat };
}

/**
 * Build the team settings nav section for the active team context.
 * Returns null if no team section should be shown.
 */
async function buildTeamNav(
  store: AppStore,
  activeTeamSlug: string,
  permission: PermissionService,
  viewer: Viewer
): Promise<NavSection[] | null> {
  const group = await store.dispatch(
    fetchGroup(activeTeamSlug, {
      transactions: true,
      subscription: true,
      aboutPage: true,
      profileKeywordsDefinition: true,
      internalNetwork: true,
      domain: true,
      savedSearches: true,
    })
  );

  if (!group) return null;

  await store.dispatch(fetchPrices(group.billing_account?.id));

  const perms = await permission.allowedBatch([
    { service: 'group', action: 'view_settings', resource: group.id },
    { service: 'group', action: 'update', resource: group.id },
    { service: 'group', action: 'update_higher_tier_settings', resource: group.id },
    { service: 'group', action: 'update_admin_settings', resource: group.id },
  ]);

  const [canViewSettings, canUpdateGroup, _canUpdateHigherTierSettings, canUpdateAdminSettings] =
    perms;
  const isSMB = group.billing_account?.isSMBAccount;
  const teamPrefix = `team/${activeTeamSlug}`;
  const teamItems: NavItem[] = [];

  if (canViewSettings.allowed) {
    teamItems.push({
      title: 'Members',
      path: `${teamPrefix}/members`,
      component: TeamMembers,
      componentProps: { groupId: group.id },
    });
  }

  if (canViewSettings.allowed && group.billing_account) {
    if (isSMB) {
      teamItems.push({
        title: 'Billing',
        path: `${teamPrefix}/billing`,
        component: SubscriptionSettings,
        componentProps: {
          account: group.billing_account,
          isOwner: canUpdateGroup.allowed,
        },
      });
    } else {
      teamItems.push({
        title: 'Billing',
        path: `${teamPrefix}/billing`,
        component: CreditSettings,
        componentProps: {
          account: group.billing_account,
          purchasePath: `/settings/${teamPrefix}/billing/purchase`,
          accountType: formatAccountType(group.account_type),
          seatCount: group.billing_account?.seat_count,
        },
      });
    }
  }

  if (canUpdateGroup.allowed) {
    if (!isSMB && group.billing_account) {
      teamItems.push({
        title: 'Purchase Credits',
        path: `${teamPrefix}/billing/purchase`,
        hidden: true,
        component: CreditPurchaseForm,
        componentProps: {
          account: group.billing_account,
          purchaseDonePath: `/settings/${teamPrefix}/billing`,
          groupId: group.id,
        },
      });
    }
    teamItems.push({
      title: 'Preferences',
      path: `${teamPrefix}/preferences`,
      component: Preferences,
      componentProps: { groupId: group.id },
    });
  }

  // About Page — gated by update_higher_tier_settings but appears deprecated
  // (not visible in QA/prod). Keeping code path for reference; re-enable if needed.
  // if (canUpdateHigherTierSettings?.allowed) {
  //   teamItems.push({
  //     title: 'About Page',
  //     path: `${teamPrefix}/about_page`,
  //     component: AboutPage,
  //     componentProps: { groupId: group.id, isViewerAdmin: viewer.admin },
  //   });
  // }

  const adminItems: NavItem[] = [];

  if (group.account_type === 'enterprise' && canUpdateAdminSettings?.allowed) {
    adminItems.push(
      {
        title: 'Subdomain',
        path: `${teamPrefix}/subdomain`,
        component: Subdomain,
        componentProps: { groupId: group.id, isViewerAdmin: viewer.admin },
      },
      {
        title: 'Saved Searches',
        path: `${teamPrefix}/saved_searches`,
        component: SavedSearches,
        componentProps: { groupId: group.id },
      },
      {
        title: 'Branding',
        path: `${teamPrefix}/branding`,
        component: Branding,
        componentProps: { groupId: group.id, isViewerAdmin: viewer.admin },
      }
    );
  }

  if (teamItems.length === 0 && adminItems.length === 0) return null;

  const teamName = (group as { name?: string }).name;
  const sections: NavSection[] = [];

  if (teamItems.length > 0) {
    sections.push({
      title: teamName ? `Teams \u2014 ${teamName}` : 'Teams',
      items: teamItems,
    });
  }

  if (adminItems.length > 0) {
    sections.push({ title: 'Admin Only', items: adminItems });
  }

  return sections;
}

/** Resolve the active team slug from URL param or current user context. */
function resolveTeamSlug(
  teamSlug: string | null,
  viewer: Viewer,
  ui: { userContext: string; userContextOptions: Array<{ value: string }> }
): string | null {
  if (teamSlug) return teamSlug;

  const groupContexts = ui.userContextOptions.filter((o) => isGroupContext(o.value as UserContext));
  if (groupContexts.length === 0) return null;

  if (isGroupContext(ui.userContext as UserContext)) {
    const contextGroup = viewer.groups?.find((g) => g.id === ui.userContext);
    return contextGroup?.slug ?? null;
  }

  return viewer.groups?.[0]?.slug ?? null;
}

const route: LegacyRoute = {
  path: '/settings/*',

  async action({ store, params, query, permission }) {
    const splat = params['*'];
    const { isTeam, teamSlug, path } = parsePath(splat);

    // Handle legacy /settings/credits → /settings/billing redirects
    if (!isTeam && path && path.startsWith('credits')) {
      const newPath = path.replace('credits', 'billing');
      return redirect(`/settings/${newPath}`);
    }

    // ENG-6300: the password form moved into Sign-in & Security.
    if (!isTeam && path === 'password') {
      const search = query.toString();
      return redirect(search ? `/settings/security?${search}` : '/settings/security');
    }

    const error = query.get('error');
    const { viewer } = store.getState();
    const accountId = viewer.billing_account_id;
    const hasTeamBillingAccount =
      viewer.groups?.some(
        (g: { billing_account?: { id?: string } | null }) => g.billing_account?.id
      ) ?? false;

    // --- Fetch personal data ---
    await Promise.all([
      store.dispatch(
        fetchUser(viewer.username, {
          addresses: true,
          otpAuthEnabled: true,
          expertRequestCandidates: true,
        })
      ),
      store.dispatch(fetchCountries()),
      store.dispatch(fetchProfile(viewer.username!, { force: true })),
      store.dispatch(fetchPrices(accountId)),
      ...(accountId ? [store.dispatch(fetchBillingAccount(accountId))] : []),
    ]);

    if (error) {
      let errorMessage;
      switch (error) {
        // ENG-5722 removed the only producer of this value, but not the URL:
        // every expert the bug hit was sent here, so it is in their history and
        // in the screenshots support asked them for. Revisiting it now lands on
        // a healthy settings page, so it must say nothing rather than fall to
        // the generic arm and raise a false alarm.
        case 'account_not_found':
          break;
        case 'country_unsupported':
          errorMessage =
            'Your country is not supported for automated payouts. Please contact support.';
          break;
        // ENG-5723. `country_required` lands on /settings/location, so the
        // toast names the form the expert is already looking at rather than
        // sending them somewhere else.
        case 'country_required':
          errorMessage = 'Add your country below, then try setting up payouts again.';
          break;
        // Read from the shared defaults rather than restated here. A redirect
        // cannot carry the api's field-naming message, so the toast shows the
        // field-less version — but it should be the SAME field-less version the
        // drawer shows, and two string literals drift. (The engagements drawer
        // shows the specific one.)
        case 'payout_setup_rejected':
          errorMessage = getDefaultErrorMessage(ERROR_CODES.PAYOUT_SETUP_REJECTED);
          break;
        case 'payout_setup_retry_later':
          errorMessage = getDefaultErrorMessage(ERROR_CODES.PAYOUT_SETUP_RETRY_LATER);
          break;
        // Pre-existing, and already the copy this case wants, so the new
        // payout.setup_failed route reuses this slug rather than adding one.
        case 'payout_setup_failed':
          errorMessage =
            'Something went wrong setting up your payout account. Please try again or contact support.';
          break;
        default:
          errorMessage = 'An error occurred. Please try again later.';
          break;
      }
      if (errorMessage) {
        store.dispatch(notify(errorMessage, 'error'));
      }
    }

    const { billing } = store.getState();
    const account = accountId
      ? // @ts-expect-error TS(7053) billing.accounts is untyped record
        billing.accounts[accountId]
      : { account_type: 'basic', entity_type: 'individual' };

    // --- Personal settings nav ---
    const personalItems: NavItem[] = [
      { title: 'Emails', path: 'emails', component: Emails },
      { title: 'Location', path: 'location', component: Location },
      { title: 'Communication', path: 'communication', component: Communication },
      { title: 'Sign-in & Security', path: 'security', component: SignInSecurity },
    ];

    const requesterItems: NavItem[] = [
      {
        title: 'Billing',
        path: 'billing',
        component: CreditSettings,
        componentProps: {
          account,
          purchasePath: '/settings/billing/purchase',
          accountType: formatAccountType(account.account_type),
          seatCount: account.seat_count,
        },
      },
      {
        title: 'Purchase Credits',
        path: 'billing/purchase',
        hidden: true,
        component: CreditPurchaseForm,
        componentProps: { account, purchaseDonePath: '/settings/billing' },
      },
      {
        title: 'Transactions',
        path: 'transactions',
        component: BillingSettings,
        componentProps: { account },
      },
    ];

    const expertItems: NavItem[] = [
      { title: 'Rate & Availability', path: 'rate', component: Rate },
      { title: 'Privacy', path: 'privacy', component: Privacy },
    ];
    if (config.enablePayoutDashboard) {
      expertItems.push({
        title: 'Payout Dashboard',
        path: 'payout',
        url: true,
        // @ts-expect-error TS(2322) legacy: div placeholder for external payout dashboard
        component: 'div',
      });
    }

    // --- Build nav ---
    const nav: NavSection[] = [
      { title: 'Settings', items: personalItems },
      ...(!hasTeamBillingAccount && accountId
        ? [{ title: 'Requester', items: requesterItems }]
        : []),
    ];

    // --- Team settings section (for current context team) ---
    const activeTeamSlug = resolveTeamSlug(teamSlug, viewer, store.getState().ui);

    if (activeTeamSlug) {
      const teamSections = await buildTeamNav(store, activeTeamSlug, permission, viewer);
      if (teamSections) {
        nav.push(...teamSections);
      }
    }

    // Expert section — only for users with expert state
    const isExpert = viewer.signup_type === 'expert' || viewer.expert_state;
    if (isExpert) {
      nav.push({ title: 'Expert', items: expertItems });
    }

    // --- Inject shared props and resolve active section ---
    const sections = nav.map((section) => ({
      ...section,
      items: section.items.map((item) => ({
        ...item,
        componentProps: {
          ...item.componentProps,
          userId: viewer.id,
          profileId: viewer.profile!.id,
        },
      })),
    }));

    const activePath = isTeam && teamSlug ? (path ? `team/${teamSlug}/${path}` : null) : path;

    let section;
    if (!activePath) {
      section = sections[0].items[0];
    } else {
      for (const part of sections) {
        section = part.items.find((e) => e.path === activePath);
        if (section) break;
      }
    }

    if (!section) {
      return <NotFoundPage />;
    }

    return (
      <LayoutPage showNav selected="settings">
        <Helmet>
          <title>{section.title} Settings</title>
        </Helmet>
        <ConsultationsStarting />
        <Settings pathPrefix="/settings" nav={sections} {...section} />
      </LayoutPage>
    );
  },
};

export default route;
